import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * How long a license explanation keeps the floor. One failed request reaches its generic handlers
 * in several steps: the subscriber's error handler runs synchronously, an `httpResource` error
 * `effect` only after the next change-detection flush, an awaited continuation a tick later
 * still. The window therefore has to outlive a rendered frame; a tenth of a second does, and is
 * short enough that a failure of an unrelated request rarely lands inside it.
 */
const BLOCKING_ERROR_WINDOW_MS = 100;

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly _snackBar = inject(MatSnackBar);

  private suppressGenericErrorsUntil = 0;

  showPersistentError(message: string, button: string) {
    if (this.isSuppressed()) return;
    this._snackBar.open(message, button);
  }

  showTemporaryError(message: string, button: string, duration: number = 5000) {
    if (this.isSuppressed()) return;
    this._snackBar.open(message, button, { duration });
  }

  /**
   * An error the user's own input caused — an incomplete form, an unreadable image — with no
   * request behind it. Never suppressed: it answers the action just taken, so an explanation left
   * over from an unrelated failed request must not swallow it.
   */
  showValidationError(message: string) {
    this._snackBar.open(message, 'Zavřít', { duration: 5000 });
  }

  showSuccess(message: string, duration: number = 4000) {
    this._snackBar.open(message, 'Zavřít', { duration });
  }

  /**
   * Shows an error that explains *why* a request was refused (expired license, spent AI credit,
   * …) and keeps the calling service's generic "Chyba při…" message from overwriting it — the
   * snack bar shows one message at a time, and the specific one is the useful one.
   *
   * The suppression lasts {@link BLOCKING_ERROR_WINDOW_MS}, long enough to cover every way the
   * same failure surfaces afterwards. A generic message of another request that happens to fall
   * inside the window is dropped, but not lost: its call site reports it before showing it.
   */
  showBlockingError(message: string, button = 'Zavřít') {
    this.suppressGenericErrorsUntil = Date.now() + BLOCKING_ERROR_WINDOW_MS;
    this._snackBar.open(message, button);
  }

  private isSuppressed(): boolean {
    return Date.now() < this.suppressGenericErrorsUntil;
  }
}
